import { Component, OnInit, ViewChild, Input, Output, EventEmitter } from '@angular/core';
import { NgForm } from '@angular/forms';
import { UtilService, NotificationService } from '../../../shared/index';
import { ActivatedRoute } from '@angular/router';
import { MomentService } from '../../../shared/moment/moment.service';

import { EventV4ItemService } from '../../shared/items/event-v4-item.service';
import { EventItemSummary } from '../../shared/items/event-item-summary.model';
import { EventItemEdit } from '../../shared/items/event-item-edit.model';
import { EventEdit } from '../../shared/event-edit.model';
import { AsyncState } from '../../../shared/model/index';
import { RevenueItem } from '../../../revenue/shared/revenue-items/revenue-item.model';
import { UserService } from '../../../users/shared/user.service';
import { AffCodeSelectorComponent } from '../../../affcode/affcode-selector/affcode-selector.component';
import { TranslationService } from '../../../shared/translation/translation.service';
import { EventV4RegistrationService } from '../../shared/registration/event-v4-registration.service';
import { RegistrationSearchParams } from '../../shared/registration/registration-search-params.model';
import { RegistrationSearchResult } from '../../shared/registration/registration-search-result.model';
import { moveItemInArray } from '@angular/cdk/drag-drop';
import { PaymentTypeDto } from 'src/app/revenue/shared/payment-types/dto/payment-type.dto';
import { PaymentTypeService } from 'src/app/revenue/shared/payment-types/payment-type.service';
import { SelectItem } from 'primeng/api';
import { AccountingPackageService } from 'src/app/revenue/shared/accounting-package/accounting-package.service';
import { AccountingPackageAdjustmentType } from 'src/app/revenue/shared/accounting-package/accounting-package-adjustment-type.model';
import { EventV4ItemDetailsModalComponent } from './event-v4-item-details-modal/event-v4-item-details-modal.component';

@Component({
  selector: 'wl-event-v4-settings-items',
  templateUrl: 'event-v4-settings-items.component.html',
  styleUrls: ['event-v4-settings-items.component.css'],
  providers: [UserService, EventV4RegistrationService],
    standalone: false
})
export class EventV4SettingsItemsComponent implements OnInit {
  @Output() public eventItemsListChange: EventEmitter<void> = new EventEmitter<void>(true);
  @ViewChild('affCodeSelectorComponent', { static: false }) affCodeSelectorComponent: AffCodeSelectorComponent;
  @ViewChild(EventV4ItemDetailsModalComponent) _eventDetailsModal: EventV4ItemDetailsModalComponent;

  @Input() public event: EventEdit;
  public _eventItemToDelete: EventItemSummary = null;
  public eventItems: Array<EventItemSummary>;
  public eventId: number;
  public selectedEventItem: EventItemEdit;
  public userAccessLevel: number;

  public loadingEventItem: boolean = false;
  public saving: boolean = false;
  public editError: string = '';
  public deleting: boolean = false;
  public deleteError: string = '';
  public checkingDelete: AsyncState = new AsyncState();
  public eventItemHasRegistrations: boolean = false;
  public loadingList: AsyncState = new AsyncState();
  public originalItem: EventItemEdit = new EventItemEdit();
  public paymentTypeItems: SelectItem[] = [];
  public adjustmentTypeItems: SelectItem[] = [];

  public showEditModal = false;
  public showDeleteModal = false;
  public get showEventItemPriceChangeWarning(): boolean {
    return (
      this.originalItem &&
      this.selectedEventItem &&
      this.selectedEventItem.EventItemId > 0 &&
      this.eventItemHasRegistrations &&
      (this.selectedEventItem.MembersOnly != this.originalItem.MembersOnly ||
        this.selectedEventItem.HasMemberPricing != this.originalItem.HasMemberPricing ||
        this.selectedEventItem.NonMemberPrice != this.originalItem.NonMemberPrice ||
        this.selectedEventItem.MemberPrice != this.originalItem.MemberPrice ||
        this.selectedEventItem.AllowRelateProfileMemberPrice != this.originalItem.AllowRelateProfileMemberPrice)
    );
  }

  public get revenueItemRequired(): boolean {
    const nonMemberPrice: number = Number(this.selectedEventItem?.NonMemberPrice) || 0;
    const memberPrice: number = Number(this.selectedEventItem?.MemberPrice) || 0;
    return nonMemberPrice > 0 || memberPrice > 0;
  }

  /** Business validation rules (return true when form should be considered invalid) */
  private _businessInvalidators: Array<() => boolean> = [];

  public constructor(
    private _route: ActivatedRoute,
    private _utilService: UtilService,
    private _eventItemService: EventV4ItemService,
    private _userService: UserService,
    private _momentService: MomentService,
    public _notificationService: NotificationService,
    public _translationService: TranslationService,
    public _registrationService: EventV4RegistrationService,
    private _paymentTypeService: PaymentTypeService,
    private _accountingPackageService: AccountingPackageService
  ) {
    this.eventItems = new Array<EventItemSummary>();
    this.eventId = 0;
    this.selectedEventItem = new EventItemEdit();
    this.userAccessLevel = 0;
  }

  public openEventDetailsModal() {
    this._eventDetailsModal.open(this.eventId);
  }

  public newEventItem = (): void => {
    this.editError = '';
    this.loadingEventItem = true;
    this.eventItemHasRegistrations = false;
    this.showEditModal = true;
    this._eventItemService
      .getNewEventItem(this.eventId)
      .then((item: EventItemEdit) => {
        this.selectedEventItem = item;

        if (this.event) {
          this.selectedEventItem.InvoiceDateDue = this._momentService.getMomentByDate(this.event.StartDate).startOf('day').toDate();
          this.selectedEventItem.EndDate = this._momentService.getMomentByDate(this.event.StartDate).startOf('day').toDate();
        }

        this.loadingEventItem = false;
      })
      .catch((error) => {
        this.loadingEventItem = false;
        this.editError = this._utilService.extractErrorMessage(error);
      });
  };

  public editEventItem = (eventItemId: number): void => {
    this.loadingEventItem = true;
    this.editError = '';
    this.eventItemHasRegistrations = false;
    this.showEditModal = true;

    // check if it has registrations
    var params: RegistrationSearchParams = new RegistrationSearchParams();
    params.EventId = this.eventId;
    params.PageNumber = 1;
    params.PageSize = 1;
    params.EventItemIds = [eventItemId];

    var promises: Promise<any>[] = [];

    var promise = this._registrationService.searchRegistrations(params).then((result: RegistrationSearchResult) => {
      this.eventItemHasRegistrations = result && result.Result && result.Result.length > 0;
    });

    promises.push(promise);

    promise = this._eventItemService.getEventItemToEdit(eventItemId).then((item: EventItemEdit) => {
      this.selectedEventItem = item;
      this.originalItem = <EventItemEdit>JSON.parse(JSON.stringify(item));
      this.affCodeSelectorComponent.modelChange(item.AffCodes);
      return this.loadPaymentTypesForItem(item.ItemNum);
    });

    promises.push(promise);

    promise = Promise.all(promises).then(
      () => {
        this.loadingEventItem = false;
        this.showEditModal = true;
      },
      (error) => {
        this.loadingEventItem = false;
        this.editError = this._utilService.extractErrorMessage(error);
      }
    );
  };

  public saveEventItem = (form?: NgForm): void => {
    if (form) {
      form.control.markAllAsTouched();
      if (this.isFormInvalid(form)) {
        return;
      }
    }

    this.saving = true;
    this.editError = '';

    this._eventItemService
      .upsertEventItem(this.selectedEventItem)
      .then(() => {
        this.reloadEventItems();
        this.showEditModal = false;
        this.saving = false;
        this.eventItemsListChange.emit();
        this._translationService
          .getTitlecase('events.settings.items.save-success-message')
          .then((translation) => this._notificationService.success(translation));
      })
      .catch((error) => {
        this.saving = false;
        this.editError = this._utilService.extractErrorMessage(error);
      });
  };

  public copyEventItem(eventItem: EventItemSummary): void {
    this.saving = true;
    this.editError = '';

    this._eventItemService
      .getEventItemToEdit(eventItem.EventItemId)
      .then((item: EventItemEdit) => {
        this._eventItemService.copyEventItem(item).then((data: number) => {
          this.reloadEventItems();
          this.saving = false;
          this.eventItemsListChange.emit();
          this.editEventItem(data);
        });
      })
      .catch((error) => {
        this.saving = false;
        this.editError = this._utilService.extractErrorMessage(error);
      });
  }

  public confirmDeleteEventItem(eventItem: EventItemSummary): void {
    if (!eventItem) {
      return;
    }

    this.deleteError = '';
    this._eventItemToDelete = eventItem;
    this.showDeleteModal = true;
    this.checkingDelete.busy = true;
    this.checkingDelete.success = false;
    this.checkingDelete.message = '';
    this.eventItemHasRegistrations = false;

    var params: RegistrationSearchParams = new RegistrationSearchParams();
    params.EventId = this.eventId;
    params.PageNumber = 1;
    params.PageSize = 1;
    params.EventItemIds = [eventItem.EventItemId];

    this._registrationService.searchRegistrations(params).then(
      (result: RegistrationSearchResult) => {
        this.checkingDelete.busy = false;
        this.checkingDelete.success = true;
        this.eventItemHasRegistrations = result && result.Result && result.Result.length > 0;
      },
      (error: any) => {
        this.checkingDelete.busy = false;
        this.checkingDelete.success = false;
        this.checkingDelete.message = this._utilService.extractErrorMessage(error);
      }
    );
  }

  public deleteEventItemConfirmed(): void {
    if (!this._eventItemToDelete) {
      return;
    }

    this.deleting = true;
    this.deleteError = '';

    // delete it
    this._eventItemService
      .deleteEventItem(this._eventItemToDelete.EventItemId)
      .then(() => {
        this.deleting = false;

        this.reloadEventItems();
        this.showDeleteModal = false;
        this.eventItemsListChange.emit();
      })
      .catch((error) => {
        this.deleting = false;
        this.deleteError = this._utilService.extractErrorMessage(error);
      });
  }

  public flatMembers = [
    { value: 'FlatRate', display: 'Flat Rate' },
    { value: 'Member', display: 'Member/NonMember' },
  ];

  public reloadEventItems() {
    this.loadingList.busy = true;
    this.loadingList.message = '';

    this._eventItemService.deleteEventItemsCache(this.eventId);

    this._eventItemService
      .getEventItems(this.eventId)
      .then((data: Array<EventItemSummary>) => {
        this.eventItems = data;
        this.loadingList.busy = false;
        this.loadingList.success = true;
      })
      .catch((error) => {
        this.loadingList.busy = false;
        this.loadingList.success = false;
        this.loadingList.message = this._utilService.extractErrorMessage(error);
      });
  }

  public revenueItemChanged = (data: RevenueItem) => {
    this.selectedEventItem.Taxable = data.Taxable;
    this.selectedEventItem.NonMemberPrice = data.UnitPrice;
    this.selectedEventItem.UnitPrice = data.UnitPrice;
    this.loadPaymentTypesForItem(data.ItemNum);
    this.loadAdjustmentTypesForItem(data.ItemNum);
  };
  ngOnInit() {
    this._route.params.subscribe((params) => {
      if (this._utilService.isNumeric(params['id'])) {
        let id = Number(params['id']);
        this.eventId = id;
        this.reloadEventItems();
      }
    });

    this._userService.getCurrentUserSecurityElementAccessLevel('Events_Module').then((data: number) => {
      this.userAccessLevel = data;
    });

    this.addRevenueItemBusinessValidator();
    this.addNameMaxLengthBusinessValidator();
  }

  /**
   * ensure number isn't null
   * @param n
   */
  public notNull(n: number): number {
    return n == null ? 0 : n;
  }

  /**
   * handle reorder by drag and drop
   */
  public handleEventItemDrop(event) {
    moveItemInArray(this.eventItems, event.previousIndex, event.currentIndex);
    let eventItemIdsNewOrder: Array<number> = this.eventItems.map((x) => x.EventItemId);

    this._eventItemService.reorderEventItems(this.eventId, eventItemIdsNewOrder);
  }

  /**
   * when has member pricing cb is checked
   */
  public handleEventItemEditHasMemberPricingChecked() {
    if (this.selectedEventItem.HasMemberPricing) {
      this.selectedEventItem.AllowRelateProfileMemberPrice = true;
    }
  }

  /**
   * when turning on members only cb is changed
   */
  public handleMembersOnlyChanged = (): void => {
    if (this.selectedEventItem.MembersOnly) {
      this.selectedEventItem.AllowRelateProfileMemberPrice = true;
      this.selectedEventItem.MemberPrice = this.selectedEventItem.NonMemberPrice;
    } else {
      this.selectedEventItem.MemberPrice = this.selectedEventItem.NonMemberPrice;
    }
  };

  public isFormInvalid(form: NgForm): boolean {
    if (!form) {
      return true;
    }
    if (form.invalid) {
      return true;
    }
    if (this._businessInvalidators.some((rule) => rule())) {
      return true;
    }
    if (!this.validateCreditTypes()) {
      return true;
    }

    return false;
  }

  private loadPaymentTypesForItem(itemNum: number) {
    return this._paymentTypeService.getPaymentTypesForRevenueItem(itemNum).then((data: Array<PaymentTypeDto>) => {
      const creditCardTypes = data.filter((x) => x.CreditCardType);
      this.paymentTypeItems = creditCardTypes.map<SelectItem>((x) => {
        return { label: x.PaymentType, value: x.PaymentTypeId };
      });
    });
  }

  private loadAdjustmentTypesForItem(itemNum: number) {
    return this._accountingPackageService
      .getAdjustmentTypesAvailableForRevenueItem(itemNum)
      .then((data: Array<AccountingPackageAdjustmentType>) => {
        this.adjustmentTypeItems = data.map<SelectItem>((x) => {
          return { label: x.AdjustmentType, value: x.AdjustmentTypeId };
        });
      });
  }

  private addRevenueItemBusinessValidator(): void {
    this._businessInvalidators.push(() => {
      const nonMemberPrice: number = Number(this.selectedEventItem?.NonMemberPrice) || 0;
      const memberPrice: number = Number(this.selectedEventItem?.MemberPrice) || 0;
      const requiresRevenueItem: boolean = nonMemberPrice > 0 || memberPrice > 0;
      return requiresRevenueItem && !this.selectedEventItem?.ItemNum;
    });
  }

  private addNameMaxLengthBusinessValidator(): void {
    this._businessInvalidators.push(() => {
      const length = (this.selectedEventItem?.Descr || '').length;
      return length > 250;
    });
  }

  private validateCreditTypes(): boolean {
    if (this.selectedEventItem.CreditTypes && this.selectedEventItem.CreditTypes.length > 0) {
      return !this.selectedEventItem.CreditTypes.some(x => x.CreditTypeId < 1 || x.CreditTypeId === null || x.CreditTypeId === undefined);
    }
    return true;
  }
}
